import { useState, useMemo } from 'react';
import {
  useAgentPipelines,
  useSaveAgentPipeline,
  useDeleteAgentPipeline,
  useAssignProjectPipeline,
} from '../hooks/useAgentPipelines';
import { useAgentTemplates } from '../hooks/useAgentLibrary';
import useProjectStore from '../stores/projectStore';
import Modal from '../components/ui/Modal';

const STEP_KINDS = [
  { kind: 'agent',        icon: '◬', label: 'Agent-Schritt',  color: 'bg-[#0D3B50] text-white' },
  { kind: 'wait',         icon: '◷', label: 'Wartezeit',       color: 'bg-amber-100 text-amber-700' },
  { kind: 'approval',     icon: '✓', label: 'Freigabe',        color: 'bg-violet-100 text-violet-700' },
  { kind: 'notification', icon: '✉', label: 'Benachrichtigung', color: 'bg-emerald-100 text-emerald-700' },
];
const KIND_META = Object.fromEntries(STEP_KINDS.map((s) => [s.kind, s]));

export default function AgentPipelines() {
  const { data, isLoading } = useAgentPipelines();
  const [editing, setEditing] = useState(null);
  const pipelines = data?.pipelines || [];

  const currentProjectId = useProjectStore((s) => s.currentProjectId);
  const currentProject = useProjectStore((s) =>
    s.projects.find((p) => p.id === s.currentProjectId)
  );
  const assignToProject = useAssignProjectPipeline();
  const activePipelineId = currentProject?.active_pipeline_id || null;

  return (
    <div className="p-4 lg:p-6 max-w-6xl mx-auto">
      <div className="flex items-end justify-between gap-4 mb-5 flex-wrap">
        <div>
          <h1 className="text-[22px] font-semibold tracking-tight text-[#0D3B50]">Agent-Pipelines</h1>
          <p className="text-[13.5px] text-gray-500 mt-1 max-w-2xl">
            Linearer Datenfluss-Builder. Jede Pipeline ist eine Sequenz aus Agent-Läufen,
            Wartezeiten, Freigaben und Benachrichtigungen. Eine Pipeline lässt sich pro
            Projekt aktivieren.
          </p>
        </div>
        <button
          onClick={() => setEditing({ steps: [] })}
          className="bg-[#0D3B50] text-white text-[13px] px-3.5 py-2 rounded-lg hover:opacity-90"
        >
          + Neue Pipeline
        </button>
      </div>

      {currentProjectId && (
        <ActiveBanner
          projectName={currentProject?.name}
          activePipeline={pipelines.find((p) => p.id === activePipelineId)}
          pipelines={pipelines}
          onChange={(pipelineId) => assignToProject.mutate({ projectId: currentProjectId, pipelineId })}
        />
      )}

      {isLoading ? (
        <div className="text-[13px] text-gray-500 p-6">Lade…</div>
      ) : pipelines.length === 0 ? (
        <div className="bg-white rounded-xl border border-black/[.07] p-8 text-center">
          <div className="text-[14px] text-gray-700 font-medium mb-1">Noch keine Pipelines</div>
          <div className="text-[12.5px] text-gray-500">Leg deine erste Pipeline oben rechts an.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {pipelines.map((p) => (
            <PipelineCard
              key={p.id}
              pipeline={p}
              isActive={p.id === activePipelineId}
              onEdit={() => setEditing(p)}
            />
          ))}
        </div>
      )}

      {editing && <PipelineEditor pipeline={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function ActiveBanner({ projectName, activePipeline, pipelines, onChange }) {
  return (
    <div className="bg-[#0D3B50]/[.04] border border-[#0D3B50]/15 rounded-xl px-4 py-3 mb-4 flex items-center gap-3 flex-wrap">
      <div className="text-[12.5px] text-gray-600">
        Aktive Pipeline für <strong className="text-[#0D3B50]">{projectName}</strong>:
      </div>
      <select
        value={activePipeline?.id || ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
        className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-[12.5px] outline-none focus:border-[#0D3B50]"
      >
        <option value="">— keine —</option>
        {pipelines.map((p) => (
          <option key={p.id} value={p.id}>{p.name}</option>
        ))}
      </select>
      {activePipeline && (
        <span className="text-[11.5px] text-gray-500">{activePipeline.steps?.length || 0} Schritte</span>
      )}
    </div>
  );
}

function PipelineCard({ pipeline, isActive, onEdit }) {
  const del = useDeleteAgentPipeline();
  return (
    <div className={`bg-white rounded-xl border p-4 shadow-sm ${isActive ? 'border-[#0D3B50]/40 ring-1 ring-[#0D3B50]/15' : 'border-black/[.07]'}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-[14.5px] text-[#0D3B50]">{pipeline.name}</h3>
            {isActive && <span className="text-[10.5px] px-2 py-0.5 rounded-full bg-[#0D3B50] text-white">aktiv</span>}
            {!pipeline.is_active && <span className="text-[10.5px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">inaktiv</span>}
          </div>
          {pipeline.description && <p className="text-[12.5px] text-gray-600 mt-1">{pipeline.description}</p>}
          <div className="text-[11.5px] text-gray-400 mt-0.5">{pipeline.slug}</div>
        </div>
      </div>

      <StepFlowPreview steps={pipeline.steps || []} />

      <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
        <button onClick={onEdit} className="text-[12px] px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50">Bearbeiten</button>
        <button
          onClick={() => { if (confirm(`Pipeline „${pipeline.name}" löschen?`)) del.mutate(pipeline.id); }}
          className="text-[12px] px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg ml-auto"
        >
          Löschen
        </button>
      </div>
    </div>
  );
}

function StepFlowPreview({ steps }) {
  if (!steps?.length) {
    return <div className="text-[12px] text-gray-400 italic mt-3">Noch keine Schritte.</div>;
  }
  return (
    <div className="flex items-center gap-1 flex-wrap mt-3 -mx-1">
      {steps.map((s, i) => {
        const meta = KIND_META[s.kind] || KIND_META.agent;
        return (
          <div key={s.id || i} className="flex items-center gap-1">
            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11.5px] ${meta.color}`}>
              <span className="font-bold">{meta.icon}</span>
              <span className="font-medium">{s.label || meta.label}</span>
            </div>
            {i < steps.length - 1 && <span className="text-gray-300">→</span>}
          </div>
        );
      })}
    </div>
  );
}

// ─── Pipeline-Editor ────────────────────────────────────────────────────────

function PipelineEditor({ pipeline, onClose }) {
  const isNew = !pipeline?.id;
  const [form, setForm] = useState({
    name: pipeline.name || '',
    slug: pipeline.slug || '',
    description: pipeline.description || '',
    is_active: pipeline.is_active !== false,
  });
  const [steps, setSteps] = useState(() =>
    (pipeline.steps || []).map((s) => ({ ...s, _localId: cryptoId() }))
  );
  const [error, setError] = useState(null);

  const save = useSaveAgentPipeline();

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const addStep = (kind) => {
    const meta = KIND_META[kind];
    setSteps((s) => [
      ...s,
      { _localId: cryptoId(), kind, label: meta.label, config: defaultConfig(kind) },
    ]);
  };
  const updateStep = (localId, patch) =>
    setSteps((s) => s.map((st) => (st._localId === localId ? { ...st, ...patch } : st)));
  const removeStep = (localId) =>
    setSteps((s) => s.filter((st) => st._localId !== localId));
  const moveStep = (localId, dir) =>
    setSteps((s) => {
      const i = s.findIndex((st) => st._localId === localId);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= s.length) return s;
      const out = [...s];
      [out[i], out[j]] = [out[j], out[i]];
      return out;
    });

  const submit = async () => {
    setError(null);
    try {
      await save.mutateAsync({
        id: pipeline.id,
        ...form,
        steps: steps.map(({ _localId, id, ...rest }) => rest),
      });
      onClose();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="w-[min(820px,94vw)] max-h-[90vh] overflow-y-auto">
        <h2 className="text-[18px] font-semibold text-[#0D3B50] mb-4">
          {isNew ? 'Neue Pipeline' : `Pipeline „${pipeline.name}"`}
        </h2>

        <div className="space-y-3 mb-5">
          <Field label="Name">
            <input
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="z.B. SEO → Reporting"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal"
            />
          </Field>
          <Field label="Beschreibung">
            <input
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              maxLength={500}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal"
            />
          </Field>
          <Field label="Slug (optional)">
            <input
              value={form.slug}
              onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal font-mono"
            />
          </Field>
          <label className="flex items-center gap-2 text-[12.5px] text-gray-700">
            <input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} />
            Aktiv (auswählbar pro Projekt)
          </label>
        </div>

        <h3 className="font-semibold text-[14px] text-[#0D3B50] mb-2">Schritte</h3>

        <div className="space-y-2 mb-3">
          {steps.length === 0 && (
            <div className="bg-gray-50 border border-dashed border-gray-200 rounded-lg p-4 text-[12.5px] text-gray-500 text-center">
              Noch keine Schritte. Unten einen hinzufügen.
            </div>
          )}
          {steps.map((s, i) => (
            <StepCard
              key={s._localId}
              step={s}
              index={i}
              total={steps.length}
              prev={steps[i - 1]}
              onChange={(patch) => updateStep(s._localId, patch)}
              onUp={() => moveStep(s._localId, -1)}
              onDown={() => moveStep(s._localId, 1)}
              onRemove={() => removeStep(s._localId)}
            />
          ))}
        </div>

        <div className="flex items-center gap-2 flex-wrap mb-5">
          <span className="text-[12px] text-gray-500">Schritt hinzufügen:</span>
          {STEP_KINDS.map((k) => (
            <button
              key={k.kind}
              type="button"
              onClick={() => addStep(k.kind)}
              className="text-[12px] px-2.5 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 inline-flex items-center gap-1.5"
            >
              <span className="font-bold">{k.icon}</span> {k.label}
            </button>
          ))}
        </div>

        {error && <div className="text-[12px] text-red-700 bg-red-50 rounded-md px-3 py-2 mb-3">{error}</div>}

        <div className="flex gap-2 pt-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-[13px] border border-gray-200 rounded-lg hover:bg-gray-50">
            Abbrechen
          </button>
          <button
            onClick={submit}
            disabled={save.isPending || !form.name.trim()}
            className="ml-auto px-5 py-2 text-[13px] bg-[#0D3B50] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
          >
            {save.isPending ? 'Speichere…' : 'Speichern'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function StepCard({ step, index, total, prev, onChange, onUp, onDown, onRemove }) {
  const meta = KIND_META[step.kind] || KIND_META.agent;
  // Soft-Warning: Notification ohne vorgeschaltete Approval
  const warnNoApproval = step.kind === 'notification' && (!prev || prev.kind !== 'approval');
  return (
    <div className={`border rounded-lg p-3 ${warnNoApproval ? 'border-amber-300 bg-amber-50/30' : 'border-gray-200'}`}>
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-[14px] ${meta.color}`}>
          {meta.icon}
        </div>
        <div className="flex-1 min-w-0">
          <input
            value={step.label || ''}
            onChange={(e) => onChange({ label: e.target.value })}
            placeholder={meta.label}
            className="w-full font-medium text-[13.5px] text-[#0D3B50] bg-transparent border-0 outline-none"
          />
          <StepConfigEditor step={step} onChange={(config) => onChange({ config })} />
          {warnNoApproval && (
            <div className="mt-2 flex items-start gap-1.5 text-[11.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
              <span className="font-bold">⚠</span>
              <span>
                Diese Benachrichtigung hat <strong>keine Freigabe davor</strong>. Standardmäßig sollte vor jedem
                Versand eine Approval-Stufe stehen — sonst geht der Output ungeprüft an den Empfänger.
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <button type="button" onClick={onUp} disabled={index === 0} className="text-gray-400 hover:text-[#0D3B50] disabled:opacity-30 text-[12px]">↑</button>
          <button type="button" onClick={onDown} disabled={index === total - 1} className="text-gray-400 hover:text-[#0D3B50] disabled:opacity-30 text-[12px]">↓</button>
          <button type="button" onClick={onRemove} className="text-red-500 hover:text-red-700 text-[12px]">✕</button>
        </div>
      </div>
    </div>
  );
}

function StepConfigEditor({ step, onChange }) {
  const cfg = step.config || {};
  const set = (k, v) => onChange({ ...cfg, [k]: v });

  if (step.kind === 'agent') {
    const { data } = useAgentTemplates({ active: true });
    const templates = data?.templates || [];
    return (
      <div className="mt-1.5">
        <select
          value={cfg.agent_template_id || ''}
          onChange={(e) => set('agent_template_id', e.target.value ? Number(e.target.value) : null)}
          className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-[12px] outline-none focus:border-teal bg-white"
        >
          <option value="">— Vorlage wählen —</option>
          {templates.map((t) => (
            <option key={t.id} value={t.id}>{t.display_name} ({t.kind})</option>
          ))}
        </select>
        {cfg.note && <div className="text-[11px] text-amber-600 mt-1">⚠ {cfg.note}</div>}
      </div>
    );
  }

  if (step.kind === 'wait') {
    return (
      <div className="mt-1.5 flex items-center gap-2">
        <input
          type="number"
          min={1}
          value={cfg.days || ''}
          onChange={(e) => set('days', Number(e.target.value) || 0)}
          className="w-20 border border-gray-200 rounded-md px-2 py-1.5 text-[12px] outline-none focus:border-teal"
        />
        <span className="text-[12px] text-gray-500">Tage</span>
      </div>
    );
  }

  if (step.kind === 'approval') {
    return (
      <div className="mt-1.5 space-y-1.5">
        <input
          value={cfg.approver || ''}
          onChange={(e) => set('approver', e.target.value)}
          placeholder="Wer entscheidet (z.B. david)"
          className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-[12px] outline-none focus:border-teal"
        />
        <input
          value={cfg.question || ''}
          onChange={(e) => set('question', e.target.value)}
          placeholder="Freigabe-Frage (z.B. Analyse für Versand freigegeben?)"
          className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-[12px] outline-none focus:border-teal"
        />
      </div>
    );
  }

  if (step.kind === 'notification') {
    return (
      <div className="mt-1.5 space-y-1.5">
        <div className="flex items-center gap-2">
          <select
            value={cfg.channel || 'mail'}
            onChange={(e) => set('channel', e.target.value)}
            className="border border-gray-200 rounded-md px-2 py-1.5 text-[12px] outline-none focus:border-teal bg-white"
          >
            <option value="mail">E-Mail</option>
            <option value="slack">Slack</option>
          </select>
          <input
            value={cfg.to || ''}
            onChange={(e) => set('to', e.target.value)}
            placeholder="Empfänger (z.B. contact.email)"
            className="flex-1 border border-gray-200 rounded-md px-2 py-1.5 text-[12px] outline-none focus:border-teal"
          />
        </div>
        <input
          value={cfg.template || ''}
          onChange={(e) => set('template', e.target.value)}
          placeholder="Template-Key (z.B. monthly-report)"
          className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-[12px] outline-none focus:border-teal"
        />
      </div>
    );
  }

  return null;
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[12px] font-medium text-gray-700 block mb-1">{label}</label>
      {children}
    </div>
  );
}

function defaultConfig(kind) {
  if (kind === 'wait') return { days: 7 };
  if (kind === 'approval') return { approver: '', question: '' };
  if (kind === 'notification') return { channel: 'mail', to: 'contact.email', template: '' };
  return {};
}

function cryptoId() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}
